import { useMemo, useState } from "react";
import { Phone, MessageSquare, ExternalLink, Globe, ShieldAlert, CalendarCheck } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { useCrisisResources } from "@/lib/safety/useCrisisResources";
import type { CrisisResource } from "@/lib/safety/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HelpCenter from "@/components/safety/HelpCenter";
import PreMeetChecklistPanel from "@/components/safety/PreMeetChecklistPanel";
import { useAuth } from "@/lib/auth/AuthProvider";

const CATEGORY_LABELS: Record<string, string> = {
  suicide: "Suicide & crisis",
  dv: "Domestic violence",
  sa: "Sexual assault",
  lgbtq: "LGBTQ+ support",
  sud: "Substance use",
  general: "General support",
};

const COUNTRIES: { value: string; label: string }[] = [
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "global", label: "Global / other" },
];

const ResourceCard = ({ r }: { r: CrisisResource }) => (
  <div className="rounded-2xl border border-border bg-card p-5">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {CATEGORY_LABELS[r.category] ?? r.category}
        </p>
        <h3 className="mt-1 text-base font-semibold leading-tight">{r.name}</h3>
        {r.hours && <p className="mt-1 text-xs text-muted-foreground">{r.hours}</p>}
      </div>
      {r.country == null && (
        <span className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <Globe className="h-3 w-3" /> Global
        </span>
      )}
    </div>
    <div className="mt-4 flex flex-wrap gap-2">
      {r.phone && (
        <a
          href={`tel:${r.phone}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-3 py-1.5 text-xs font-medium hover:opacity-90"
        >
          <Phone className="h-3.5 w-3.5" /> {r.phone}
        </a>
      )}
      {r.sms && (
        <a
          href={`sms:${r.sms}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary"
        >
          <MessageSquare className="h-3.5 w-3.5" /> Text {r.sms}
        </a>
      )}
      {r.url && (
        <a
          href={r.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Website
        </a>
      )}
    </div>
  </div>
);

const Safety = () => {
  const { user } = useAuth();
  const guess = typeof navigator !== "undefined" ? navigator.language.split("-")[1]?.toUpperCase() : null;
  const initial = guess && COUNTRIES.find((c) => c.value === guess) ? guess : "US";
  const [country, setCountry] = useState<string>(initial);

  const { global, community, loading } = useCrisisResources({ app: null });
  const resources = useMemo(
    () => [...global, ...community] as unknown as CrisisResource[],
    [global, community],
  );

  const grouped = useMemo(() => {
    const m = new Map<string, CrisisResource[]>();
    for (const r of resources) {
      const key = r.category ?? "general";
      const list = m.get(key) ?? [];
      list.push(r);
      m.set(key, list);
    }
    return Array.from(m.entries());
  }, [resources]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative">
          <div className="absolute inset-x-0 top-0 h-1 bg-wavlynq-gradient" aria-hidden />
          <div className="mx-auto max-w-4xl px-6 pt-20 pb-12">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
              Safety Center
            </p>
            <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight">
              Help is here, in any moment.
            </h1>
            <p className="mt-5 max-w-2xl text-muted-foreground leading-relaxed">
              Wavlynq is built with safety at the center. If you're in immediate danger,
              use your local emergency number. For everything else — crisis support, blocking,
              reporting, ID verification — start here.
            </p>

            <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-5 flex items-start gap-4">
              <ShieldAlert className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">In immediate danger?</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Use the floating Emergency Button (signed-in members) or dial your local
                  emergency number now. In the US: <a href="tel:911" className="underline font-medium text-foreground">911</a>.
                  In the UK: <a href="tel:999" className="underline font-medium text-foreground">999</a>.
                  EU: <a href="tel:112" className="underline font-medium text-foreground">112</a>.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-4xl px-6 py-12">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Crisis resources</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  24/7 hotlines and support, vetted and curated.
                </p>
              </div>
              <div className="w-48">
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
            ) : grouped.length === 0 ? (
              <p className="mt-8 text-sm text-muted-foreground">
                No resources for that region yet. Try Global / other.
              </p>
            ) : (
              <div className="mt-8 space-y-8">
                {grouped.map(([cat, list]) => (
                  <div key={cat}>
                    <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                      {CATEGORY_LABELS[cat] ?? cat}
                    </h3>
                    <div className="mt-3 grid gap-4 md:grid-cols-2">
                      {list.map((r) => (
                        <ResourceCard key={r.id} r={r} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {user && (
          <section className="border-t border-border" id="pre-meet">
            <div className="mx-auto max-w-4xl px-6 py-16">
              <div className="flex items-start gap-3">
                <CalendarCheck className="h-6 w-6 text-primary shrink-0 mt-1" aria-hidden />
                <div className="flex-1">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    Before you meet
                  </p>
                  <h2 className="mt-2 text-2xl font-bold tracking-tight">
                    Pre-meet checklist
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Plan the venue, lock in your safe word, and hand a courtesy
                    PDF to your Trusted Circle — all in 60 seconds.
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <PreMeetChecklistPanel />
              </div>
            </div>
          </section>
        )}

        <section className="border-t border-border">
          <div className="mx-auto max-w-4xl px-6 py-16">
            <HelpCenter />
          </div>
        </section>

        <section className="border-t border-border bg-secondary/30">
          <div className="mx-auto max-w-4xl px-6 py-16 grid gap-10 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Built in, not bolted on
              </p>
              <h3 className="mt-3 text-xl font-semibold">Every member, every tier.</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li>• Emergency Button — locale-aware dial, geolocation, recording.</li>
                <li>• Crisis-aware RAELA — always-on, trained for hard moments.</li>
                <li>• ID verification on every member before discovery.</li>
                <li>• Block + Report on every profile and message.</li>
                <li>• Verified mentors with background checks.</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Your controls
              </p>
              <h3 className="mt-3 text-xl font-semibold">Yours to shape.</h3>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li>• Customize your Emergency Button — color, label, position.</li>
                <li>• Incognito mode — free, every tier.</li>
                <li>• AI training contribution — opt in or out anytime.</li>
                <li>• Blocked list — manage from Account.</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Safety;
